import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRightIcon } from "lucide-react";
import type { CaseStudyContent } from "../../data/caseStudies";
import { CaseStudySection } from "./CaseStudySection";
import { BODY, EYEBROW, H2, H3 } from "../../utils/typography";
type FeaturesSectionProps = {
  features: NonNullable<CaseStudyContent["features"]>;
};
const ease = [0.23, 1, 0.32, 1] as const;

/** Headline feature gets a dark panel; supporting features sit in a quiet two-column list. */
export function FeaturesSection({
  features
}: FeaturesSectionProps) {
  const {
    lead,
    items
  } = features;
  const hasVisual = Boolean(lead.visual);
  return <CaseStudySection label={features.heading} data-id="element-70" data-mp="ak3kpyn6fxmy">
      <article className={`grid grid-cols-1 gap-10 rounded-3xl bg-espresso p-6 md:p-10 ${hasVisual ? "xl:grid-cols-[minmax(0,1fr)_18rem] xl:items-center" : ""}`} data-id="element-71" data-mp="alejfk1rmf77">
        <div data-id="element-72" data-mp="n8k9a7cfn61">
          <p className={`${EYEBROW} text-mustard`} data-id="element-73" data-mp="mpdmpn58l7b">{lead.eyebrow}</p>
          <h3 className={`mt-4 ${H2} text-cream`} data-id="element-74" data-mp="1upcfgbix24s4">{lead.title}</h3>
          <div className="mt-5 space-y-4" data-id="element-75" data-mp="1zgu543fhoixs">
            {lead.body.map(p => <p key={p} className={`${BODY} text-cream/80`} data-id="element-76" data-mp="1zix6fjm3dyht">
                {p}
              </p>)}
          </div>
        </div>
        {lead.visual === "chat" && <ChatIllustration data-id="element-77" data-mp="1prspiecuw3kc" />}
        {lead.visual === "exchange" && <ExchangeIllustration data-id="element-78" data-mp="2dgc0j1naiug4" />}
      </article>

      <ul className="mt-14 grid grid-cols-1 gap-x-10 gap-y-10 md:grid-cols-2" data-id="element-79" data-mp="2dhn0srhj1k2h">
        {items.map(item => <li key={item.title} data-id="element-80" data-mp="2idvlekehu5h1">
            <h3 className={`${H3} text-espresso`} data-id="element-81" data-mp="23j79anel88sg">{item.title}</h3>
            <p className={`mt-3 ${BODY} text-espresso/70`} data-id="element-82" data-mp="23l1wk8dc0czp">{item.body}</p>
          </li>)}
      </ul>
    </CaseStudySection>;
}
function useAppear() {
  const reduce = useReducedMotion();
  return (delay: number) => reduce ? {} : {
    initial: {
      opacity: 0,
      y: 8,
      scale: 0.96
    },
    whileInView: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    viewport: {
      once: true,
      amount: 0.6
    },
    transition: {
      duration: 0.25,
      ease,
      delay
    }
  };
}

/** Abstract chat exchange: a question, a typing pause, then an answer. Decorative only. */
function ChatIllustration() {
  const reduce = useReducedMotion();
  const appear = useAppear();
  return <div aria-hidden="true" className="flex flex-col gap-3 rounded-2xl bg-bark p-5" data-id="element-83" data-mp="2l04hbhntlz4y">
      <motion.div {...appear(0.1)} className="ml-auto w-3/4 space-y-2 rounded-2xl rounded-br-md bg-cream/15 px-4 py-3" data-id="element-84" data-mp="2g2usa34cfphn">
        <span className="block h-2 w-full rounded-full bg-cream/50" data-id="element-85" data-mp="2c1st1maffdfo" />
        <span className="block h-2 w-2/3 rounded-full bg-cream/50" data-id="element-86" data-mp="271ejfzt6fvfj" />
      </motion.div>

      <motion.div {...appear(0.45)} className="flex w-16 items-center justify-center gap-1 rounded-2xl rounded-bl-md bg-mustard/90 px-3 py-3" data-id="element-87" data-mp="222k10yz4g8vy">
        {[0, 1, 2].map(i => <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-espresso" animate={reduce ? undefined : {
        opacity: [0.3, 1, 0.3]
      }} transition={{
        duration: 0.9,
        repeat: Infinity,
        delay: i * 0.15
      }} data-id="element-88" data-mp="9njctapi82v" />)}
      </motion.div>

      <motion.div {...appear(0.9)} className="w-5/6 space-y-2 rounded-2xl rounded-bl-md bg-mustard px-4 py-3" data-id="element-89" data-mp="hjrapepfek7k">
        <span className="block h-2 w-full rounded-full bg-espresso/60" data-id="element-90" data-mp="miu7kbv95k4g" />
        <span className="block h-2 w-full rounded-full bg-espresso/60" data-id="element-91" data-mp="3l07zaue13yf6" />
        <span className="block h-2 w-1/2 rounded-full bg-espresso/60" data-id="element-92" data-mp="3l2ax40lfavug" />
      </motion.div>
    </div>;
}

/** Abstract value exchange: a short name form, then the offer it unlocks. Decorative only. */
function ExchangeIllustration() {
  const appear = useAppear();
  return <div aria-hidden="true" className="flex flex-col gap-3 rounded-2xl bg-bark p-5" data-id="element-93" data-mp="3sur2ddh9dcn">
      <motion.div {...appear(0.1)} className="space-y-2 rounded-2xl bg-cream/10 p-4" data-id="element-94" data-mp="1a73rf88wnxsy">
        <span className="block h-1.5 w-1/3 rounded-full bg-cream/40" data-id="element-95" data-mp="1f6gloe23w75k" />
        <span className="block h-7 rounded-lg ring-1 ring-cream/25" data-id="element-96" data-mp="1cslagbwf0ifx" />
        <span className="block h-7 rounded-lg ring-1 ring-cream/25" data-id="element-97" data-mp="1csuz9w5q4jk1" />
        <span className="mt-1 block h-7 w-1/2 rounded-full bg-mustard" data-id="element-98" data-mp="18s18mv0wcqng" />
      </motion.div>

      <motion.div {...appear(0.4)} className="flex justify-center" data-id="element-99" data-mp="1mrskap3xxuya">
        <ArrowRightIcon className="h-4 w-4 rotate-90 text-cream/40" data-id="element-100" data-mp="1hrxtelk615d8" />
      </motion.div>

      <motion.div {...appear(0.7)} className="flex items-center gap-3 rounded-2xl bg-mustard p-4" data-id="element-101" data-mp="1vrfn9fcbj8tg">
        <span className="h-10 w-8 shrink-0 rounded-md bg-espresso/80" data-id="element-102" data-mp="1rpu3rb5a3waa" />
        <span className="flex-1 space-y-2" data-id="element-103" data-mp="1rplqhi58uoyk">
          <span className="block h-2 w-full rounded-full bg-espresso/60" data-id="element-104" data-mp="25hs52zsr0nvw" />
          <span className="block h-2 w-2/3 rounded-full bg-espresso/60" data-id="element-105" data-mp="25j38gyia7tmh" />
        </span>
      </motion.div>
    </div>;
}